/**
 * Управление источником из интерфейса: вес и включённость поверх реестра (решение Ильи
 * 15.10.2026). Хранение — `migrations/0021_source_overrides.sql`, домен —
 * `services/backend/src/domain/source-controls.ts`, правило применения у мониторинга —
 * `services/agent-monitor/src/source-overrides.ts`.
 *
 * ПРАВИЛО ОДНО И ПО КАЖДОМУ ПОЛЮ ОТДЕЛЬНО: действует `отклонение ?? реестр`. Реестр
 * (`config/sources/<brand>.yaml`) — умолчание, правка из интерфейса — отклонение, `null` в
 * отклонении — «как в реестре». Поэтому в теле правки `null` значит «сбросить к реестру», а
 * отсутствие поля — «не трогать»: это два разных намерения, и смешать их значило бы сбрасывать
 * вес нажатием на выключатель.
 *
 * ДИАПАЗОН ВЕСА ИЗ ИНТЕРФЕЙСА УЖЕ, ЧЕМ У РЕЕСТРА. Реестр допускает (0, 1]
 * (`SourceEntrySchema.weight`); ручка интерфейса — 0.1…1.0 шагом 0.01. Нижняя граница — потому
 * что вес 0.01 читается как «выключен», а выключают источник выключателем, а не нулём (тот же
 * довод, что у реестра: «источник с нулевым весом отключают, а не обнуляют»). Шаг — потому что
 * реестр держит сотые (0.72 у profound и seroundtable), а значение вроде 0.7300000001 из ползунка
 * засоряло бы журнал. Проверяет маршрут (422), а не схема тела: число правильной формы, но вне
 * правил ручки — это «нельзя», а не «тело кривое» (тот же принцип, что у потолков батчей в
 * api/runs.ts).
 */

import * as z from 'zod';
import { ISO_UTC_MS } from '../time.ts';
import { SOURCE_CODE_RE } from './source-registry.ts';

const IsoUtcMs = z.string().regex(ISO_UTC_MS);

export const SOURCE_WEIGHT_MIN = 0.1;
export const SOURCE_WEIGHT_MAX = 1;
export const SOURCE_WEIGHT_STEP = 0.01;

/**
 * Вес, который можно поставить из интерфейса: в [0.1, 1] и кратный 0.01. Кратность
 * проверяется с допуском: 0.15 в двоичной записи — не ровно 0.15, и деление на шаг без допуска
 * отбило бы половину значений ползунка.
 */
export function isSourceWeightSettable(w: number): boolean {
  if (!Number.isFinite(w)) return false;
  if (w < SOURCE_WEIGHT_MIN - 1e-9 || w > SOURCE_WEIGHT_MAX + 1e-9) return false;
  const steps = w / SOURCE_WEIGHT_STEP;
  return Math.abs(steps - Math.round(steps)) < 1e-6;
}

/** Каноническая запись веса: две цифры после точки, без хвоста двоичной дроби. */
export function canonicalSourceWeight(w: number): number {
  return Math.round(w * 100) / 100;
}

/** Слово здоровья — CHECK `source.health` (0002 §3). */
export const SOURCE_HEALTH_STATES = ['unknown', 'ok', 'degraded', 'dead'] as const;
export type SourceHealthState = (typeof SOURCE_HEALTH_STATES)[number];

/**
 * Тело `PATCH /api/v1/sources/:code?brand=`. Поле нет — не трогать; `null` — сбросить к
 * реестру. Пустое тело — 400: запрос, который ничего не меняет, но пишет строку журнала,
 * оставил бы в аудите «изменил», которого не было.
 */
export const SourceOverridePatchBodySchema = z
  .strictObject({
    weight: z.number().nullable().optional(),
    enabled: z.boolean().nullable().optional(),
    /**
     * Оптимистичная блокировка: `override.changed_at`, который видел человек; `null` — строки
     * отклонения ещё не было. Не совпало — 409 `source_override_conflict`: источник успели
     * изменить в другой вкладке, и молча перезаписать чужую правку значило бы потерять её.
     * Нет поля — проверки нет (вызов не из интерфейса).
     */
    expected_changed_at: IsoUtcMs.nullable().optional(),
  })
  .refine((b) => b.weight !== undefined || b.enabled !== undefined, {
    error: 'нужно хотя бы одно поле: weight или enabled',
  });
export type SourceOverridePatchBody = z.output<typeof SourceOverridePatchBodySchema>;

/**
 * Один источник бренда на вкладке «Мониторинг»: что сказал реестр, что поставили руками, что
 * из этого действует, и здоровье. Действующие `weight` и `enabled` отдаёт бэкенд, а не
 * выводит экран: правило «отклонение ?? реестр» живёт в одном месте (SQL домена), а не в
 * двух.
 */
/**
 * Выход источника — то, чего не видит окно здоровья (ревью 18.10.2026, №3). Здоровье судит
 * ленту: ответила ли она и разобралась ли. OpenAI News 18.10 был здоров на 100% и при этом
 * не дал ни одного материала: лента — тизеры по 150 знаков, каждая статья — 403 Cloudflare,
 * и пересчёт длины отсеивал всё. Выход — воронка за окно: найдено → статья скачана →
 * материал прошёл порог длины.
 *
 *   * `unknown` — обходов в окне не было;
 *   * `quiet` — лента отвечает, нового нет (тихий источник — не поломка);
 *   * `none` — материалы или статьи были, а ни один материал не прошёл порог длины;
 *   * `low` — статьи в основном не скачиваются (меньше половины из ≥3), материалы держатся на
 *     тексте ленты — тизер вместо статьи;
 *   * `ok` — иначе.
 */
export const SOURCE_YIELD_STATES = ['unknown', 'quiet', 'none', 'low', 'ok'] as const;
export type SourceYieldState = (typeof SOURCE_YIELD_STATES)[number];
/** Окно воронки выхода, сутки: две недели — минимум два обхода у самых тихих расписаний. */
export const SOURCE_YIELD_WINDOW_DAYS = 14;

export interface SourceYieldCounts {
  readonly runs: number;
  readonly items_found: number;
  readonly articles_tried: number;
  readonly articles_ok: number;
  readonly materials: number;
  readonly too_short: number;
}

/** Одно правило на бэкенд (выдача) и тесты; интерфейс читает готовое слово. */
export function sourceYieldStateOf(y: SourceYieldCounts): SourceYieldState {
  if (y.runs === 0) return 'unknown';
  const usable = y.materials - y.too_short;
  if (y.materials === 0 && y.articles_tried === 0) return 'quiet';
  if (usable <= 0) return 'none';
  if (y.articles_tried >= 3 && y.articles_ok * 2 < y.articles_tried) return 'low';
  return 'ok';
}

const Count = z.int().nonnegative();

export const SourceYieldSchema = z.strictObject({
  window_days: z.int().positive(),
  /** Прогонов, в которых источник обходился. */
  runs: Count,
  /** Элементов ленты/листинга, найденных за окно (сумма по прогонам, повторы включены). */
  items_found: Count,
  /** Статей, которые пытались скачать (одна статья прогона — одна попытка, хопы внутри). */
  articles_tried: Count,
  /** Из них — с текстом статьи. */
  articles_ok: Count,
  /** Из неудачных — 401/403/429/451 или антибот-заглушка. */
  articles_blocked: Count,
  /** Материалов источника, впервые увиденных в окне. */
  materials: Count,
  /** Из них отсеяно `too_short`. */
  too_short: Count,
  state: z.enum(SOURCE_YIELD_STATES),
});
export type SourceYield = z.output<typeof SourceYieldSchema>;

export const SourceControlSchema = z.strictObject({
  source_code: z.string().regex(SOURCE_CODE_RE),
  title: z.string().nullable(),
  home_url: z.string().nullable(),
  registry: z.strictObject({
    weight: z.number(),
    enabled: z.boolean(),
    /** Комментарий над `enabled:` в YAML — почему реестр источник выключил. */
    note: z.string().nullable(),
  }),
  override: z.strictObject({
    weight: z.number().nullable(),
    enabled: z.boolean().nullable(),
    /** Последняя правка из интерфейса, в том числе сброс; `null` — руками не трогали. */
    changed_by_login: z.string().nullable(),
    changed_at: IsoUtcMs.nullable(),
  }),
  /** Действующие значения: `override ?? registry`. */
  weight: z.number(),
  enabled: z.boolean(),
  health: z.strictObject({
    state: z.enum(SOURCE_HEALTH_STATES),
    last_attempt_at: IsoUtcMs.nullable(),
    last_success_at: IsoUtcMs.nullable(),
    cursor_at: IsoUtcMs.nullable(),
    last_error_text: z.string().nullable(),
    attempts_10: z.int().nonnegative(),
    /** Доля успешных из окна; `null` — попыток ещё не было (не ноль). */
    success_ratio_10: z.number().min(0).max(1).nullable(),
  }),
  /**
   * Строка-заглушка реестра (html без разметки): включить её нельзя ни файлом, ни отсюда —
   * PATCH `enabled: true` отвечает 422 `placeholder_source` (ревью №17).
   */
  placeholder: z.boolean(),
  yield: SourceYieldSchema,
});
export type SourceControl = z.output<typeof SourceControlSchema>;

/** Ответ `GET /api/v1/sources?brand=` — все источники реестра бренда, включённые и нет. */
export const SourceControlsResponseSchema = z.strictObject({
  brand: z.string().min(1),
  sources: z.array(SourceControlSchema),
});
export type SourceControlsResponse = z.output<typeof SourceControlsResponseSchema>;
